import { createContext, useContext, useEffect, useReducer } from "react";
import { toast } from "sonner";
import { ACHIEVEMENTS, CHARACTER_IDS, WORLD_IDS } from "@/data";
import { sound } from "@/lib/sound";

const KEY = "webtoonverse_v1";

const initial = {
  playerName: null,
  visited: {},
  secrets: {},
  story: {},
  perfectGifts: {},
  minigames: {},
  achievements: {},
  chatCounts: {},
  authorUnlocked: false,
  cinematicSeen: false,
  settings: { muted: false, music: false, koreanSubs: false },
};

function load() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY));
    if (!raw) return initial;
    return { ...initial, ...raw, settings: { ...initial.settings, ...(raw.settings || {}) } };
  } catch {
    return initial;
  }
}

function reducer(state, action) {
  switch (action.type) {
    case "VISIT":
      return { ...state, visited: { ...state.visited, [action.world]: true } };
    case "UNLOCK_SECRET":
      return { ...state, secrets: { ...state.secrets, [action.char]: true } };
    case "SET_STORY":
      return { ...state, story: { ...state.story, [action.world]: action.choice } };
    case "PERFECT_GIFT":
      return { ...state, perfectGifts: { ...state.perfectGifts, [action.char]: action.item } };
    case "MINIGAME":
      return { ...state, minigames: { ...state.minigames, [action.world]: true } };
    case "SET_NAME":
      return { ...state, playerName: action.name };
    case "CHAT":
      return {
        ...state,
        chatCounts: { ...state.chatCounts, [action.char]: (state.chatCounts[action.char] || 0) + 1 },
      };
    case "ACHIEVE":
      return { ...state, achievements: { ...state.achievements, [action.id]: true } };
    case "AUTHOR":
      return { ...state, authorUnlocked: true };
    case "CINEMATIC_SEEN":
      return { ...state, cinematicSeen: true };
    case "SETTING":
      return { ...state, settings: { ...state.settings, [action.key]: action.value } };
    case "RESET":
      return { ...initial };
    default:
      return state;
  }
}

export function computeCompletion(state) {
  let done = 0;
  const total = WORLD_IDS.length * 3 + CHARACTER_IDS.length * 2; // 9 + 12 = 21
  WORLD_IDS.forEach((w) => {
    if (state.visited[w]) done++;
    if (state.story[w]) done++;
    if (state.minigames[w]) done++;
  });
  CHARACTER_IDS.forEach((c) => {
    if (state.secrets[c]) done++;
    if (state.perfectGifts[c]) done++;
  });
  return { done, total, pct: Math.round((done / total) * 100) };
}

export function worldBreakdown(state, worldId, charIds) {
  let done = 0;
  const total = 3 + charIds.length * 2;
  if (state.visited[worldId]) done++;
  if (state.story[worldId]) done++;
  if (state.minigames[worldId]) done++;
  charIds.forEach((c) => {
    if (state.secrets[c]) done++;
    if (state.perfectGifts[c]) done++;
  });
  return { done, total, pct: Math.round((done / total) * 100) };
}

const GameContext = createContext(null);

export function GameProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, undefined, load);

  // persist
  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(state));
  }, [state]);

  // keep sound mute in sync
  useEffect(() => {
    sound.setMuted(state.settings.muted);
  }, [state.settings.muted]);

  // evaluate achievements
  useEffect(() => {
    const a = state.achievements;
    const grant = (id) => {
      if (a[id]) return;
      dispatch({ type: "ACHIEVE", id });
      const def = ACHIEVEMENTS.find((x) => x.id === id);
      toast(`🏆 ${def.title}`, { description: def.titleKr });
      sound.playAchievement();
    };
    if (Object.values(state.chatCounts).some((n) => n > 0)) grant("first_chat");
    if (Object.keys(state.secrets).length > 0) grant("first_secret");
    if (WORLD_IDS.every((w) => state.visited[w])) grant("all_worlds");
    if (Object.keys(state.perfectGifts).length > 0) grant("perfect_gift");
    if (WORLD_IDS.every((w) => state.story[w])) grant("all_story");
    if (CHARACTER_IDS.every((c) => state.secrets[c])) grant("all_secrets");
    if (WORLD_IDS.every((w) => state.minigames[w])) grant("minigame_master");
    const { pct } = computeCompletion(state);
    if (pct >= 100) {
      grant("completionist");
      if (!state.authorUnlocked) dispatch({ type: "AUTHOR" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const completion = computeCompletion(state);

  const api = {
    state,
    completion,
    visit: (world) => dispatch({ type: "VISIT", world }),
    unlockSecret: (char) => {
      if (!state.secrets[char]) {
        dispatch({ type: "UNLOCK_SECRET", char });
        sound.playUnlock();
      }
    },
    setStory: (world, choice) => dispatch({ type: "SET_STORY", world, choice }),
    givePerfectGift: (char, item) => dispatch({ type: "PERFECT_GIFT", char, item }),
    completeMinigame: (world) => {
      if (!state.minigames[world]) dispatch({ type: "MINIGAME", world });
    },
    setName: (name) => dispatch({ type: "SET_NAME", name }),
    addChat: (char) => dispatch({ type: "CHAT", char }),
    setSetting: (key, value) => dispatch({ type: "SETTING", key, value }),
    markCinematicSeen: () => dispatch({ type: "CINEMATIC_SEEN" }),
    reset: () => {
      dispatch({ type: "RESET" });
      sound.stopMusic();
    },
  };

  return <GameContext.Provider value={api}>{children}</GameContext.Provider>;
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error("useGame must be used within GameProvider");
  return ctx;
}
